import { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const normalize = (value) => String(value ?? "").trim();

export const getEmployeeDisplayName = (employee) => {
  if (!employee) return "";
  const name = normalize(employee.emp_name || employee.user_name || employee.emp_id);
  const empNo = normalize(employee.emp_no);
  return empNo ? `${name} (${empNo})` : name;
};

export const getEmployeeDetailText = (employee) =>
  [
    employee?.team_name || employee?.department_name,
    employee?.position_name,
    employee?.emp_status === 1 ? "휴직" : employee?.emp_status === 2 ? "퇴사" : "",
  ]
    .map(normalize)
    .filter(Boolean)
    .join(" · ");

export const getEmployeeSearchText = (employee) =>
  [
    employee?.emp_id,
    employee?.emp_no,
    employee?.emp_name,
    employee?.user_name,
    employee?.team_name,
    employee?.department_name,
    employee?.position_name,
  ]
    .map(normalize)
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export const getEmployeeTeamKey = (employee) =>
  normalize(employee?.team_id || employee?.department_id || "__none__");

export const getEmployeeTeamName = (employee) =>
  normalize(employee?.team_name || employee?.department_name || "팀 미지정");

export function getEmployeeTeams(employees = []) {
  const teamMap = new Map();
  employees.forEach((employee) => {
    const key = getEmployeeTeamKey(employee);
    const name = getEmployeeTeamName(employee);
    if (!teamMap.has(key)) teamMap.set(key, { key, name, count: 0 });
    teamMap.get(key).count += 1;
  });
  return Array.from(teamMap.values()).sort((a, b) => a.name.localeCompare(b.name, "ko"));
}

export function filterEmployeesByKeyword(employees = [], keyword = "") {
  const search = normalize(keyword).toLowerCase();
  if (!search) return employees;
  return employees.filter((employee) => getEmployeeSearchText(employee).includes(search));
}

export function EmployeeSelect({
  employees = [],
  value,
  onChange,
  label = "담당자",
  placeholder = "이름, 사번, 팀으로 검색",
  helperText,
  required = false,
  disabled = false,
  size = "small",
  fullWidth = true,
  allowEmpty = true,
  teamFilter = true,
  sx,
}) {
  const [selectedTeam, setSelectedTeam] = useState("all");
  const teams = useMemo(() => getEmployeeTeams(employees), [employees]);
  const filteredEmployees = useMemo(() => {
    if (!teamFilter || selectedTeam === "all") return employees;
    return employees.filter((employee) => getEmployeeTeamKey(employee) === selectedTeam);
  }, [employees, selectedTeam, teamFilter]);
  const selectedEmployee = useMemo(
    () => employees.find((employee) => String(employee.emp_id) === String(value || "")) || null,
    [employees, value]
  );
  const autocompleteOptions = useMemo(() => {
    if (
      selectedEmployee &&
      !filteredEmployees.some((employee) => String(employee.emp_id) === String(selectedEmployee.emp_id))
    ) {
      return [selectedEmployee, ...filteredEmployees];
    }
    return filteredEmployees;
  }, [filteredEmployees, selectedEmployee]);

  const handleChange = (_, employee) => {
    onChange(employee ? String(employee.emp_id) : "");
  };

  return (
    <Stack spacing={1} sx={sx}>
      {teamFilter ? (
        <TextField
          select
          size={size}
          label="팀 선택"
          value={selectedTeam}
          onChange={(event) => setSelectedTeam(event.target.value)}
          disabled={disabled || !teams.length}
          fullWidth={fullWidth}
        >
          <MenuItem value="all">전체 팀</MenuItem>
          {teams.map((team) => (
            <MenuItem key={team.key} value={team.key}>
              {team.name} ({team.count}명)
            </MenuItem>
          ))}
        </TextField>
      ) : null}
      <Autocomplete
        options={autocompleteOptions}
        value={selectedEmployee}
        onChange={handleChange}
        disabled={disabled}
        size={size}
        fullWidth={fullWidth}
        clearOnEscape={allowEmpty}
        disableClearable={!allowEmpty}
        noOptionsText="선택할 근로자가 없습니다."
        groupBy={teamFilter ? getEmployeeTeamName : undefined}
        getOptionLabel={(employee) => getEmployeeDisplayName(employee)}
        isOptionEqualToValue={(option, selected) =>
          String(option.emp_id) === String(selected.emp_id)
        }
        filterOptions={(options, state) => filterEmployeesByKeyword(options, state.inputValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            helperText={helperText}
            required={required}
          />
        )}
        renderOption={(props, employee) => {
          const { key, ...optionProps } = props;
          return (
            <Box component="li" {...optionProps} key={key || employee.emp_id}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, width: "100%" }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 800 }} noWrap>
                    {getEmployeeDisplayName(employee)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {getEmployeeDetailText(employee) || "소속 미지정"}
                  </Typography>
                </Box>
                {employee.emp_status === 1 ? <Chip size="small" label="휴직" /> : null}
                {employee.emp_status === 2 ? <Chip size="small" color="default" label="퇴사" /> : null}
              </Stack>
            </Box>
          );
        }}
      />
    </Stack>
  );
}

export function EmployeeBulkPicker({
  employees = [],
  selectedIds = [],
  onSelectEmployee,
  onSelectEmployees,
  onDeselectEmployees,
  renderSecondaryAction,
  title = "근로자 선택",
  helperText = "팀을 먼저 선택하거나 이름·사번으로 검색할 수 있습니다.",
  searchPlaceholder = "이름, 사번, 팀으로 검색",
  emptyText = "선택할 근로자가 없습니다.",
  selectedLabel = "선택",
  scopeLabel,
  teamFilter = true,
  maxVisible = 250,
  listHeight = { xs: 340, md: 440 },
  fixedListHeight = false,
  hideSelectedCount = false,
}) {
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [keyword, setKeyword] = useState("");
  const selectedSet = useMemo(
    () => new Set((selectedIds || []).map((id) => String(id))),
    [selectedIds]
  );
  const teams = useMemo(() => getEmployeeTeams(employees), [employees]);
  const teamFilteredEmployees = useMemo(() => {
    if (!teamFilter || selectedTeam === "all") return employees;
    return employees.filter((employee) => getEmployeeTeamKey(employee) === selectedTeam);
  }, [employees, selectedTeam, teamFilter]);
  const filteredEmployees = useMemo(
    () => filterEmployeesByKeyword(teamFilteredEmployees, keyword),
    [keyword, teamFilteredEmployees]
  );
  const visibleEmployees = useMemo(
    () => filteredEmployees.slice(0, maxVisible),
    [filteredEmployees, maxVisible]
  );
  const selectedInFiltered = useMemo(
    () => filteredEmployees.filter((employee) => selectedSet.has(String(employee.emp_id))).length,
    [filteredEmployees, selectedSet]
  );
  const selectedTeamName = scopeLabel || (selectedTeam === "all"
      ? "현재 조건"
      : teams.find((team) => team.key === selectedTeam)?.name || "현재 팀");

  const handleSelectVisibleEmployee = (employee, checked) => {
    onSelectEmployee?.(employee, checked);
  };

  const handleSelectFiltered = () => {
    if (filteredEmployees.length) onSelectEmployees?.(filteredEmployees);
  };

  const handleDeselectFiltered = () => {
    if (filteredEmployees.length) onDeselectEmployees?.(filteredEmployees);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Stack spacing={1.25} sx={{ p: 1.5 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {helperText}
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            sx={{ minWidth: 0, justifyContent: { xs: "flex-start", sm: "flex-end" } }}
          >
            {!hideSelectedCount ? (
              <Chip size="small" label={`${selectedLabel} ${selectedSet.size}명`} color="primary" />
            ) : null}
            <Chip size="small" label={`검색 ${filteredEmployees.length}명`} variant="outlined" />
          </Stack>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ minWidth: 0 }}>
          {teamFilter ? (
            <TextField
              select
              size="small"
              label="팀"
              value={selectedTeam}
              onChange={(event) => setSelectedTeam(event.target.value)}
              sx={{ minWidth: { xs: "100%", sm: 180 } }}
            >
              <MenuItem value="all">전체 팀</MenuItem>
              {teams.map((team) => (
                <MenuItem key={team.key} value={team.key}>
                  {team.name} ({team.count}명)
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            sx={{ flex: 1, minWidth: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <Button
            size="small"
            variant="contained"
            disabled={!filteredEmployees.length}
            onClick={handleSelectFiltered}
            sx={{ fontWeight: 900 }}
          >
            {selectedTeamName} 전체 선택
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            disabled={!filteredEmployees.length || !selectedInFiltered}
            onClick={handleDeselectFiltered}
            sx={{ fontWeight: 900 }}
          >
            {selectedTeamName} 선택 해제
          </Button>
          {filteredEmployees.length > maxVisible ? (
            <Chip
              size="small"
              label={`${maxVisible}명만 표시 중 · 전체 선택은 ${filteredEmployees.length}명에 적용`}
              sx={{ bgcolor: "#f8fafc", fontWeight: 800 }}
            />
          ) : null}
        </Stack>
      </Stack>

      <Divider />
      <List
        disablePadding
        sx={{
          height: fixedListHeight ? listHeight : "auto",
          maxHeight: listHeight,
          overflowY: "auto",
          overflowX: "hidden",
          bgcolor: "#fff",
        }}
      >
        {visibleEmployees.map((employee) => {
          const selected = selectedSet.has(String(employee.emp_id));
          return (
            <ListItem
              key={employee.emp_id}
              divider
              secondaryAction={renderSecondaryAction?.(employee, selected)}
              sx={{
                minWidth: 0,
                py: 0.9,
                pr: renderSecondaryAction ? { xs: 11.5, sm: 13 } : 1.5,
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              <Checkbox
                checked={selected}
                onChange={(event) => handleSelectVisibleEmployee(employee, event.target.checked)}
                sx={{ mr: 0.5 }}
              />
              <ListItemText
                primary={getEmployeeDisplayName(employee)}
                secondary={getEmployeeDetailText(employee) || "소속 미지정"}
                primaryTypographyProps={{ fontWeight: 850, noWrap: true }}
                secondaryTypographyProps={{ noWrap: true }}
                sx={{ minWidth: 0 }}
              />
            </ListItem>
          );
        })}
        {!visibleEmployees.length ? (
          <ListItem>
            <ListItemText primary={emptyText} />
          </ListItem>
        ) : null}
      </List>
    </Paper>
  );
}
